/* eslint-disable react-refresh/only-export-components */

import React, { useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useU2000GridStateHandlers } from '../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { useU2000SearchStateHandlers } from '../u2000Core/reducer/formReducer/U2000SearchStateHandlers';
import type { U2000GridStateHandlersReturn } from '../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import type { U2000SearchStateHandlersReturn } from '../u2000Core/reducer/formReducer/U2000SearchStateHandlers';
import { useU2000LUStateHandlers } from '../u2000Core/reducer/formReducer/U2000LUHandlers';
import type { U2000LUState } from '../u2000Core/reducer/formReducer/U2000LUState';
import type { U3500DocumentSearchItem, U3500InitLU } from './U3500Service';

export interface U3500ContextType {
  initLU: U3500InitLU;
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U3500DocumentSearchItem>;
}

interface U3500ContextProviderProps {
  initLU: U3500InitLU;
  children: ReactNode;
}

const U3500Context = React.createContext<U3500ContextType | undefined>(undefined);

export const U3500ContextProvider: React.FC<U3500ContextProviderProps> = ({ initLU, children }) => {
  const U2000LUStateHandlers = useU2000LUStateHandlers({ initLU: initLU as U3500InitLU & U2000LUState });
  const U2000SearchStateHandlers = useU2000SearchStateHandlers({
    luName: 'u3500',
    launchSearch: initLU.launchSearch,
    hideSearch: initLU.hideSearch,
  });
  const U2000GridStateHandlers = useU2000GridStateHandlers<U3500DocumentSearchItem>({
    nbRecordPerPage: initLU.nbRecordPerPage ?? 25,
  });

  const contextValue = useMemo<U3500ContextType>(
    () => ({
      initLU,
      luStateHandlers: U2000LUStateHandlers,
      searchStateHandlers: U2000SearchStateHandlers,
      gridStateHandlers: U2000GridStateHandlers,
    }),
    [
      initLU,
      U2000GridStateHandlers,
      U2000SearchStateHandlers,
      U2000LUStateHandlers,
      U2000GridStateHandlers.getData?.()?.length,
      U2000GridStateHandlers.getTotalRows?.(),
    ]
  );

  return <U3500Context.Provider value={contextValue}>{children}</U3500Context.Provider>;
};

export function useU3500Context(): U3500ContextType {
  const context = useContext(U3500Context);
  if (!context) {
    throw new Error('useU3500Context must be used within a U3500ContextProvider');
  }
  return context;
}
